import {
  DiscountPlanKey,
  DiscountResponse,
  PlanDiscount,
} from '../services/subscriptions';
import { FREE_TRIAL_DAYS } from '../constants/pricingTiers';

type TrialPeriodFormat = 'trial' | 'banner';

/**
 * Formats the free-trial duration supplied by a plan discount. Partner offers
 * use a month-oriented label for a 30-day trial; all other values retain the
 * day-oriented label used by the standard offer.
 */
export const formatTrialPeriod = (
  trialPeriodDays: number | null | undefined,
  format: TrialPeriodFormat = 'trial'
): string => {
  const days = trialPeriodDays ?? FREE_TRIAL_DAYS;

  if (days === 30) {
    return format === 'banner' ? '1 month free' : '1-month free trial';
  }

  return `${days}-day free trial`;
};

/**
 * CTA label for starting a plan's free trial ("Start 10 Day Free Trial").
 * Deliberately day-oriented even for 30-day partner trials (ORA-377).
 */
export const formatTrialCtaLabel = (
  trialPeriodDays: number | null | undefined
): string => `Start ${trialPeriodDays ?? FREE_TRIAL_DAYS} Day Free Trial`;

/**
 * Formats a price as a bare number string (the `$` is rendered separately in
 * the UI). Whole numbers stay integer; fractional values get two decimals.
 */
export const formatPrice = (price: number): string => {
  return Number.isInteger(price) ? String(price) : price.toFixed(2);
};

/**
 * Core discount math on primitive coupon values. Percent-off and amount-off are
 * mutually exclusive; `amountOff` is in dollars. Returns the base price
 * unchanged when there is no discount. Every discount-aware surface (Pricing
 * page, per-plan selector, Billing page) delegates here so a base price and a
 * coupon always resolve to the same displayed price.
 */
export const applyDiscount = (
  basePrice: number,
  discount:
    { percentOff?: number | null; amountOff?: number | null } | null | undefined
): number => {
  if (!discount) return basePrice;

  const { percentOff, amountOff } = discount;
  let calculated = basePrice;
  // Loose `!= null` so a backend that omits a field (undefined) can't produce
  // NaN (undefined / 100).
  if (percentOff != null) calculated = basePrice * (1 - percentOff / 100);
  else if (amountOff != null) calculated = basePrice - amountOff;

  if (calculated < 0) return 0;
  return Math.round(calculated * 100) / 100;
};

/**
 * Applies an active discount to a base price. Returns the base price unchanged
 * when there is no active discount. Mirrors the logic the Pricing page uses so
 * displayed prices stay consistent between surfaces.
 */
export const computeDiscountedPrice = (
  basePrice: number,
  activeDiscount: DiscountResponse | null | undefined
): number => {
  if (!activeDiscount?.hasDiscount) return basePrice;
  return applyDiscount(basePrice, activeDiscount.discount);
};

/**
 * Resolve the discount for a plan (ORA-44).
 *
 * Per-plan responses (each plan can carry a different coupon) are authoritative:
 * when ANY plan has its own entry we trust the `plans` map exactly, so a plan
 * the campaign excludes correctly resolves to null. Legacy single-discount
 * responses (cohort / marketing-offer / referral) carry one top-level `discount`
 * and no per-plan entries — we synthesize a per-plan shape from it so those
 * discounts still render on every plan (regression guard).
 */
export const getPlanDiscount = (
  activeDiscount: DiscountResponse | null | undefined,
  planKey: DiscountPlanKey
): PlanDiscount | null => {
  if (!activeDiscount?.hasDiscount) return null;

  const plans = activeDiscount.plans;

  const hasAnyPerPlan =
    !!plans && Object.values(plans).some((plan) => plan != null);

  if (hasAnyPerPlan) {
    return plans?.[planKey] ?? null;
  }

  const discount = activeDiscount.discount;
  if (!discount) return null;

  return {
    name: discount.name,
    percentOff: discount.percentOff,
    amountOff: discount.amountOff,
    displayPercentOff: activeDiscount.source?.displayPercentOff ?? null,
    durationType: discount.durationType ?? null,
    durationMonths: discount.durationMonths ?? null,
    expiresAt: discount.expiresAt,
  };
};

// Every plan a discount payload can target; plan-agnostic helpers iterate it.
const DISCOUNT_PLAN_KEYS = [
  'MONTHLY',
  'ANNUAL',
  'FIVE_YEAR',
] as const satisfies readonly DiscountPlanKey[];

/**
 * Gets a plan's backend-authoritative trial length, retaining the standard
 * trial duration for no discount, legacy discounts, and omitted overrides.
 */
export const getPlanTrialPeriodDays = (
  activeDiscount: DiscountResponse | null | undefined,
  planKey: DiscountPlanKey
): number =>
  getPlanDiscount(activeDiscount, planKey)?.trialPeriodDays ?? FREE_TRIAL_DAYS;

/**
 * Returns a trial duration only when every plan advertises the same one. This
 * keeps plan-agnostic surfaces from promising the monthly duration for a plan
 * with a different backend-authoritative trial.
 */
export const getSharedTrialPeriodDays = (
  activeDiscount: DiscountResponse | null | undefined
): number | null => {
  const trialPeriodDays = DISCOUNT_PLAN_KEYS.map((planKey) =>
    getPlanTrialPeriodDays(activeDiscount, planKey)
  );

  return trialPeriodDays.every((days) => days === trialPeriodDays[0])
    ? trialPeriodDays[0]
    : null;
};

/**
 * A plan's trial length ONLY when the payload explicitly carries one — no
 * fallback to the standard trial. An explicit value in the authed
 * `/subscription/discount` payload is server-confirmed: the backend only
 * includes a rule the user actually matches, so it is the trial they will get
 * at checkout. Callers use this to distinguish "this offer overrides the
 * trial" from "no override, assume the standard trial".
 */
export const getExplicitPlanTrialPeriodDays = (
  activeDiscount: DiscountResponse | null | undefined,
  planKey: DiscountPlanKey
): number | null =>
  getPlanDiscount(activeDiscount, planKey)?.trialPeriodDays ?? null;

/**
 * The explicit trial override shared by every plan, or null when any plan
 * lacks one or they disagree. Plan-agnostic surfaces (banner, trial timeline)
 * use this so they only advertise a trial for offers that genuinely carry an
 * override.
 */
export const getSharedExplicitTrialPeriodDays = (
  activeDiscount: DiscountResponse | null | undefined
): number | null => {
  const trialPeriodDays = DISCOUNT_PLAN_KEYS.map((planKey) =>
    getExplicitPlanTrialPeriodDays(activeDiscount, planKey)
  );

  const [first] = trialPeriodDays;

  return first != null && trialPeriodDays.every((days) => days === first)
    ? first
    : null;
};

/**
 * Whether a plan should render trial-forward copy.
 *
 * `showTrialMessaging` is the acquisition-audience gate (logged out, or a free
 * user with no prior subscription). It is deliberately conservative and hides
 * the trial from churned users — but a discount rule can grant them a trial
 * anyway, and an explicit per-plan override in the payload means the backend
 * confirmed this user gets it. In that case we show the trial rather than
 * under-promising something checkout will hand them.
 */
export const shouldShowPlanTrialMessaging = (
  showTrialMessaging: boolean,
  activeDiscount: DiscountResponse | null | undefined,
  planKey: DiscountPlanKey
): boolean =>
  showTrialMessaging ||
  getExplicitPlanTrialPeriodDays(activeDiscount, planKey) != null;

/**
 * Apply a resolved per-plan discount to a base price. Percent-off and
 * amount-off are mutually exclusive; `amountOff` is in dollars. Returns the
 * base price unchanged when there is no plan discount.
 */
export const computePlanDiscountedPrice = (
  basePrice: number,
  planDiscount: PlanDiscount | null
): number => {
  return applyDiscount(basePrice, planDiscount);
};
